import { helpWhy } from '../../help/why';
import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { BondLicenseService } from '../../services/bondLicense.service';
import { IBondLicense, ICreateBondLicenseRequest } from '../../interfaces/commercial.interface';

interface Props {
    institutionId: string;
    target: IBondLicense | null;
    onClose: () => void;
    onSaved: () => void;
}

const EMPTY: ICreateBondLicenseRequest = { licenseNo: '', bondType: '', issueDate: null, validTill: null, status: 'ACTIVE', remarks: '' };

/** Create/edit a bond-warehouse license (T8, brief item 13.1). Mirrors
 * `UdUtilizationDialog`/`UdRegisterForm`'s save shape -- trimmed values on submit, `status` is
 * caller-supplied (no fixed create-time lifecycle value, per `CreateBondLicenseRequest`'s Javadoc). */
const BondLicenseDialog: React.FC<Props> = ({ institutionId, target, onClose, onSaved }) => {
    const toast = useToast();
    const isEdit = !!target;
    const [form, setForm] = useState<ICreateBondLicenseRequest>(
        target
            ? {
                licenseNo: target.licenseNo, bondType: target.bondType ?? '', issueDate: target.issueDate,
                validTill: target.validTill, status: target.status, remarks: target.remarks ?? '',
            }
            : EMPTY
    );
    const [saving, setSaving] = useState(false);

    const valid = form.licenseNo.trim().length > 0 && form.licenseNo.length <= 100 && form.status.trim().length > 0;

    const saveWhy = saving ? null
        : form.licenseNo.trim().length === 0 ? 'Enter the licence number.'
        : form.licenseNo.length > 100 ? 'The licence number can be 100 characters at most.'
        : form.status.trim().length === 0 ? 'Enter a status.'
        : null;

    const handleSave = async () => {
        const payload: ICreateBondLicenseRequest = {
            ...form,
            licenseNo: form.licenseNo.trim(),
            bondType: form.bondType?.trim() || null,
            status: form.status.trim(),
            remarks: form.remarks?.trim() || null,
        };
        try {
            setSaving(true);
            if (isEdit && target) {
                await BondLicenseService.update(institutionId, target.id, payload);
                toast.showSuccess('Bond license updated');
            } else {
                await BondLicenseService.create(institutionId, payload);
                toast.showSuccess('Bond license created');
            }
            onSaved();
        } catch (e) {
            toast.showApiError(e, 'Failed to save bond license');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'commercial.bondForm.page' } as any}>
            <DialogTitle>{isEdit ? 'Edit Bond License' : 'New Bond License'}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }} data-help-section="commercial.bondForm.section.details">
                <TextField data-help="commercial.bondForm.field.licenseNo" label="License No" value={form.licenseNo} required
                    onChange={e => setForm({ ...form, licenseNo: e.target.value })} />
                <TextField data-help="commercial.bondForm.field.bondType" label="Bond Type" value={form.bondType ?? ''}
                    onChange={e => setForm({ ...form, bondType: e.target.value })} />
                <TextField data-help="commercial.bondForm.field.issueDate" label="Issue Date" type="date" InputLabelProps={{ shrink: true }}
                    value={form.issueDate ?? ''} onChange={e => setForm({ ...form, issueDate: e.target.value || null })} />
                <TextField data-help="commercial.bondForm.field.validTill" label="Valid Till" type="date" InputLabelProps={{ shrink: true }}
                    value={form.validTill ?? ''} onChange={e => setForm({ ...form, validTill: e.target.value || null })} />
                <TextField data-help="commercial.bondForm.field.status" label="Status" value={form.status} required
                    onChange={e => setForm({ ...form, status: e.target.value })} />
                <TextField data-help="commercial.bondForm.field.remarks" label="Remarks" multiline minRows={2} value={form.remarks ?? ''}
                    onChange={e => setForm({ ...form, remarks: e.target.value })} />
            </DialogContent>
            <DialogActions>
                <Button data-help="commercial.bondForm.cancel" onClick={onClose}>Cancel</Button>
                <Button data-help="commercial.bondForm.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" disabled={saving || !valid} onClick={handleSave}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default BondLicenseDialog;
